import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useFetchOnDemand } from "../Hooks/fetchOnDemand";
import { debounce } from "lodash";

import "./MultiSelect.scss";

const MultiSelect = (props) => {
  const { field, formData, form, match, onFieldChange } = props;
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [fetchData, setFetchData] = useState(null);
  const [links, setLinks] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const { fetchOnDemand, token: fetchOnDemandToken } = useFetchOnDemand();

  const listData = useMemo(() => {
    return field.fetchData ? fetchData : formData[field.data];
  }, [formData[field.data], fetchData, field.fetchData]);

  const updateSearchTerm = useCallback(
    debounce((value) => setSearchTerm(value), 1000),
    [],
  );

  const buildPaginatedFetchUrl = () => {
    let url = field.fetchURL;
    url = url.replace("#sport", match.params.sport).replace("#orgId", match.params.organizationId);
    url += `?limit=${field.fetchLimit ? field.fetchLimit : 200}`;
    url += "&withLinks=true";
    return url;
  };

  const buildDependFetchUrl = (dependValue) => {
    let url = field.fetchURL;
    url = url.replace("#sport", match.params.sport).replace("#orgId", match.params.organizationId);
    return `${url}?${field.fetchDependQueryParam}=${dependValue}&limit=${field.fetchLimit ? field.fetchLimit : 200}`;
  };

  const buildSearchFetchUrl = (searchTerm) => {
    let url = field.fetchURL;
    url = url.replace("#sport", match.params.sport).replace("#orgId", match.params.organizationId);
    return `${url}?nameContains=${searchTerm}&limit=${field.fetchLimit ? field.fetchLimit : 200}`;
  };

  const buildInitialFetchUrl = (initialData) => {
    let url = field.fetchURL;
    url = url.replace("#sport", match.params.sport).replace("#orgId", match.params.organizationId);
    return `${url}?${field.valueField}=${initialData.join(',')}`
  };

  const fetchWithLink = (link) => {
    let url = link + "&withLinks=true";
    fetchOnDemand(url).then((response) => {
      setFetchData(response?.data?.data);
      setLinks(response?.data?.links);
    });
  };

  useEffect(() => {
    if (formData[field.initial] && !initialized) {
      if (field.fetchData && !!fetchOnDemandToken) {
        fetchOnDemand(buildInitialFetchUrl(formData[field.initial])).then((response) => {
          setSelectedRowsData(response?.data ?? []);
        });
        setInitialized(true);
      } else if (!!formData[field.data]) {
        setSelectedRowsData(
          formData[field.initial]
            .filter((initialRow) => formData[field.data].findIndex((row) => row[field.valueField] === initialRow) > -1)
            ?.map((initialRow) => formData[field.data].find((row) => row[field.valueField] === initialRow)),
        );
        setInitialized(true);
      }
    }
  }, [formData[field.initial], formData[field.data], initialized, fetchOnDemandToken]);

  useEffect(() => {
    if (!field.fetchData || !fetchOnDemandToken) {
      return;
    }

    const dependValue = form?.values?.[field.fetchDepend];
    if (dependValue && dependValue.length > 0) {
      Promise.all(dependValue.map((value) => fetchOnDemand(buildDependFetchUrl(value)))).then((response) => {
        const responseData = response?.data ?? response;
        if (responseData) {
          let combinedData =
            responseData?.reduce((acc, curr) => {
              if (curr) {
                return [...acc, ...curr?.data];
              }
              return acc;
            }, []) ?? [];
          setFetchData(combinedData);
        }
        setLinks(null);
      });
    } else {
      fetchOnDemand(buildPaginatedFetchUrl()).then((response) => {
        setFetchData(response?.data?.data);
        setLinks(response?.data?.links);
      });
    }
  }, [form?.values?.[field.fetchDepend], fetchOnDemandToken, field.fetchData]);

  useEffect(() => {
    if (!field.fetchData || !fetchOnDemandToken) {
      return;
    }

    if (searchTerm) {
      fetchOnDemand(buildSearchFetchUrl(searchTerm)).then((response) => {
        setFetchData(response?.data);
        setLinks(null);
      });
    } else {
      fetchOnDemand(buildPaginatedFetchUrl()).then((response) => {
        setFetchData(response?.data?.data);
        setLinks(response?.data?.links);
      });
    }
  }, [searchTerm]);

  useEffect(() => {
    const values = selectedRowsData.reduce((acc, curr) => {
      return [...acc, curr[field.valueField]];
    }, []);
    form.setFieldValue(field.name, values);
    onFieldChange && onFieldChange(field.name, values);
    // eslint-disable-next-line
  }, [selectedRowsData]);

  const visibleOptions = useMemo(() => {
    const allOptions = listData
      ?.filter((rowData) => !!rowData.name)
      .sort(function (a, b) {
        var aMixed = a["name"];
        var bMixed = b["name"];
        return aMixed < bMixed ? -1 : 1;
      });

    if (!allOptions) {
      return [];
    }

    const withoutAlreadySelectedOptions = allOptions.filter(
      (rowData) =>
        selectedRowsData.findIndex((selectedRow) => selectedRow[field.valueField] === rowData[field.valueField]) === -1,
    );

    if (!query || field.fetchData) {
      return withoutAlreadySelectedOptions;
    }

    return withoutAlreadySelectedOptions.filter((option) => option.name.toUpperCase().includes(query.toUpperCase()));
  }, [listData, query, selectedRowsData]);

  const removeItem = (item) => {
    setSelectedRowsData(selectedRowsData.filter((row) => row[field.valueField] !== item[field.valueField]));
  };

  return (
    <React.Fragment>
      <div className={`multi-select ${field.fetchData ? "paginated" : ""}`}>
        <div className="multi-select-all">
          <input
            placeholder="Search..."
            value={query}
            onChange={(ev) => {
              setQuery(ev.target.value);
              updateSearchTerm(ev.target.value);
            }}
          />
          <ul className={`multi-select-list ${field.fetchData ? "paginated" : ""}`}>
            {visibleOptions.map((item) => (
              <li
                key={`add-${item[field.valueField]}`}
                onClick={() => {
                  setSelectedRowsData([...selectedRowsData, item]);
                }}
                title={item.name}
              >
                <span>{item.name}</span>
              </li>
            ))}
          </ul>
          {field.fetchData && (
            <div className="multi-select-pagination">
              <div
                className={`multi-select-pagination-btn ${!!links?.previous ? "" : "disabled"}`}
                onClick={() => {
                  if (!!links?.previous) {
                    fetchWithLink(links.previous);
                  }
                }}
              >
                <i className="fas fa-angle-left" />
              </div>
              <div
                className={`multi-select-pagination-btn ${!!links?.next ? "" : "disabled"}`}
                onClick={() => {
                  if (!!links?.next) {
                    fetchWithLink(links.next);
                  }
                }}
              >
                <i className="fas fa-angle-right" />
              </div>
            </div>
          )}
        </div>
        <div className="multi-select-selected">
          <span className="multi-select-text">{selectedRowsData.length} item(s) selected</span>
          <ul className="multi-select-list">
            {selectedRowsData
              .sort(function (a, b) {
                var aMixed = a["name"];
                var bMixed = b["name"];
                return aMixed < bMixed ? -1 : 1;
              })
              .map((item) => (
                <li key={`remove-${item[field.valueField]}`} onClick={() => removeItem(item)}>
                  <span>{item.name}</span>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </React.Fragment>
  );
};

export default MultiSelect;
